import { motion } from 'framer-motion';
import {
  Eye,
  Target,
  Rocket,
  CheckCircle,
  ArrowRight,
  AlertTriangle,
  Users,
  Compass,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function VisionStatementPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const characteristics = [
    {
      icon: Rocket,
      title: 'Aspirational',
      description: 'Describes an ambitious future state that inspires and motivates',
    },
    {
      icon: Eye,
      title: 'Forward-Looking',
      description: "Focuses on where you're going, not where you are today",
    },
    {
      icon: Target,
      title: 'Clear & Memorable',
      description: 'Easy to understand and remember - passes the "elevator test"',
    },
    {
      icon: Users,
      title: 'Unifying',
      description: 'Brings people together around a common purpose and direction',
    },
  ];

  const examples = [
    {
      company: 'Tesla',
      vision: "To accelerate the world's transition to sustainable energy",
      why: 'Ambitious, clear purpose, addresses global challenge, inspires action',
    },
    {
      company: 'Microsoft',
      vision: 'Empower every person and organization on the planet to achieve more',
      why: 'Inclusive, aspirational, focuses on impact rather than products',
    },
    {
      company: 'Amazon',
      vision: "To be Earth's most customer-centric company",
      why: 'Clear differentiation, measurable aspiration, guides all decisions',
    },
    {
      company: 'Disney',
      vision: 'To make people happy',
      why: 'Simple, emotional, timeless, and applicable across all business units',
    },
    {
      company: 'LinkedIn',
      vision: 'Create economic opportunity for every member of the global workforce',
      why: 'Broad impact, social mission, connects to their core platform value',
    },
  ];

  const commonMistakes = [
    {
      mistake: 'Too Vague or Generic',
      example: '"Be the best company in our industry"',
      fix: 'Be specific about what "best" means and what unique impact you\'ll have',
    },
    {
      mistake: 'Too Product-Focused',
      example: '"Build the most advanced software platform"',
      fix: 'Focus on the outcome and impact, not the product itself',
    },
    {
      mistake: 'Too Limiting',
      example: '"Become the top regional provider of X"',
      fix: "Think bigger - don't artificially constrain your ambition",
    },
    {
      mistake: 'Too Complex',
      example: '3-paragraph vision statement with multiple clauses',
      fix: 'Keep it to one powerful sentence that anyone can remember',
    },
  ];

  const howToCreate = [
    {
      step: '1',
      title: 'Start with Why',
      description:
        'What change do you want to see in the world? What problem are you solving at scale?',
      questions: [
        "If we're wildly successful, what will be different in the world?",
        'What legacy do we want to leave?',
        'What impact matters most to us?',
      ],
    },
    {
      step: '2',
      title: 'Think 10+ Years Out',
      description:
        'Vision should be aspirational and long-term. What does success look like in a decade?',
      questions: [
        'What does our industry look like in 10 years?',
        'What role do we play in that future?',
        'What becomes possible if we achieve our vision?',
      ],
    },
    {
      step: '3',
      title: 'Make It Emotional',
      description: 'Great visions connect on an emotional level, not just rational. They inspire.',
      questions: [
        'What will make our team proud to work here?',
        'What will customers be grateful we exist?',
        'What will make this meaningful?',
      ],
    },
    {
      step: '4',
      title: 'Test for Clarity',
      description: 'Can anyone in your organization explain it in 30 seconds? Is it memorable?',
      questions: [
        'Would our newest employee understand this?',
        'Can people repeat it without looking?',
        'Does it actually guide decisions?',
      ],
    },
  ];

  return (
    <MarketingLayout>
      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Foundation', path: '/foundation' },
              { label: 'Vision Statement' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Compass className="w-4 h-4 mr-2" />
            Foundation Step 1 of 4
          </span>
        }
        title="A vision your team can build toward"
        subtitle="Turn an ambitious future into a clear direction. Create a vision statement that helps your team choose what matters, what to pursue, and what to leave behind."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-opacity"
        >
          Create your vision
        </Link>
      </PageHero>

      {/* What Makes a Great Vision */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">Give every decision a direction</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A useful vision earns its place in everyday decisions. Test yours against these four
              qualities before it becomes the foundation of your plan.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {characteristics.map((char, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-white p-6 rounded-xl border border-gray-200"
              >
                <div className="bg-blue-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <char.icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">{char.title}</h3>
                <p className="text-gray-600">{char.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Real-World Examples */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">
              Vision Statements from World-Class Companies
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Learn from companies that have crafted powerful, inspiring visions
            </p>
          </motion.div>

          <div className="space-y-6 max-w-4xl mx-auto">
            {examples.map((example, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-white p-6 rounded-xl border border-gray-200"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{example.company}</h3>
                <p className="text-xl text-blue-600 italic mb-4">"{example.vision}"</p>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-600">
                    <span className="font-semibold">Why it works:</span> {example.why}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Create */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">How to Create Your Vision Statement</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Follow this proven 4-step process to craft a vision that inspires
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {howToCreate.map((item, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <div className="flex items-start space-x-4 mb-4">
                  <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                    <p className="text-gray-700">{item.description}</p>
                  </div>
                </div>
                <div className="ml-16">
                  <p className="font-semibold text-gray-900 mb-2 text-sm">Key Questions:</p>
                  <ul className="space-y-2">
                    {item.questions.map((q, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="bg-blue-500 w-1.5 h-1.5 rounded-full mr-2 mt-2 flex-shrink-0" />
                        <span className="text-gray-600 text-sm">{q}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Common Vision Statement Mistakes</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Avoid these pitfalls that make vision statements ineffective
            </p>
          </motion.div>

          <div className="space-y-6 max-w-4xl mx-auto">
            {commonMistakes.map((item, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-white p-6 rounded-xl border border-red-200"
              >
                <div className="flex items-start space-x-3 mb-3">
                  <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{item.mistake}</h3>
                    <p className="text-red-600 italic mt-1">Bad example: {item.example}</p>
                  </div>
                </div>
                <div className="ml-9 bg-green-50 p-3 rounded-lg border border-green-200">
                  <p className="text-sm">
                    <span className="font-semibold text-green-700">How to fix:</span> {item.fix}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                From an idea of the future to a shared direction
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Name the future</h3>
                <p className="text-gray-600">
                  Describe the change you want your company to make, who it benefits, and what
                  success would look like.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Make it memorable</h3>
                <p className="text-gray-600">
                  Shape that ambition into a clear statement your team can understand and use.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Connect it to action</h3>
                <p className="text-gray-600">
                  Use your vision to guide the mission, values, and objectives that follow.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Foundation Elements</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/foundation/mission-statement"
                className="public-card bg-white p-6 rounded-xl transition-shadow border border-transparent hover:border-gray-200"
              >
                <Target className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="font-bold text-lg mb-2">Mission Statement</h3>
                <p className="text-gray-600 text-sm">Define your purpose and why you exist</p>
              </Link>
              <Link
                to="/foundation/core-values"
                className="public-card bg-white p-6 rounded-xl transition-shadow border border-transparent hover:border-gray-200"
              >
                <CheckCircle className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="font-bold text-lg mb-2">Core Values</h3>
                <p className="text-gray-600 text-sm">Establish principles that guide decisions</p>
              </Link>
              <Link
                to="/foundation/strategic-objectives"
                className="public-card bg-white p-6 rounded-xl transition-shadow border border-transparent hover:border-gray-200"
              >
                <Rocket className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="font-bold text-lg mb-2">Strategic Objectives</h3>
                <p className="text-gray-600 text-sm">Set measurable goals to achieve your vision</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/foundation"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                ← Back to Foundation
              </Link>
              <Link
                to="/foundation/mission-statement"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Next: Mission Statement <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">Create your vision in StratPlan</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Use your vision to guide the mission, values, and objectives that follow. Start with
            your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Create your vision <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
